/**
 * SIL-2246 U1. Pure helpers for the bench plan screens (approval page and plan builder): vocabulary labels, fit badges, the
 * wipe confirmation, duration text, the preview grouped by device and fixed error copy. No React, so it is unit testable.
 *
 * The labels mirror the owner-written catalog (migrations 1187 and 1192). The client has no catalog endpoint, so an unknown
 * area or kind falls back to its humanized code, and the server stays the authority on what a plan may contain (the save
 * definer refuses a step that is not allowed with 422 step_not_allowed).
 */
import type { BenchHardwareFound, BenchPlanPreview, BenchPlanSearchResult } from '@siliconx/shared';

export const AREA_ORDER = ['inventory', 'gpu', 'ram', 'cpu', 'storage', 'chassis'] as const;

const AREA_LABELS: Record<string, string> = {
  inventory: 'Inventory',
  gpu: 'GPUs',
  ram: 'Memory',
  cpu: 'CPUs',
  storage: 'Storage',
  chassis: 'Chassis and power',
};

export interface StepKindInfo {
  label: string;
  area: string;
  /** An org may add this kind to its own plan ("+ Add test"). The save definer re-checks. */
  orgAddable: boolean;
  destructive: boolean;
  description: string;
}

export const STEP_KINDS: Record<string, StepKindInfo> = {
  inventory_record: { label: 'Inventory record', area: 'inventory', orgAddable: false, destructive: false, description: 'Records the discovered hardware identity. Runs no test.' },
  stress: { label: 'Stress test', area: 'gpu', orgAddable: true, destructive: false, description: 'Sustained compute stress of the device with the vendor diagnostic.' },
  link_check: { label: 'Link check', area: 'gpu', orgAddable: true, destructive: false, description: 'Verifies the bus link width and speed.' },
  cpu_stress: { label: 'CPU stress test', area: 'cpu', orgAddable: true, destructive: false, description: 'Sustained all-core compute stress with result verification.' },
  memory_integrity: { label: 'Memory integrity test', area: 'ram', orgAddable: true, destructive: false, description: 'Write and read-back pattern test of installed memory.' },
  smart_health: { label: 'SMART health read', area: 'storage', orgAddable: true, destructive: false, description: 'Reads the drive health log and grades it.' },
  wipe: { label: 'Secure wipe', area: 'storage', orgAddable: true, destructive: true, description: 'Destroys all data on the drive. Needs wipe authorization during the run.' },
  firmware_record: { label: 'Firmware record', area: 'chassis', orgAddable: true, destructive: false, description: 'Records chassis and component firmware versions.' },
};

function humanize(code: string): string {
  const s = code.replace(/_/g, ' ').trim();
  return s.length === 0 ? code : s.charAt(0).toUpperCase() + s.slice(1);
}

export const areaLabel = (area: string): string => AREA_LABELS[area] ?? humanize(area);
export const kindLabel = (kind: string): string => STEP_KINDS[kind]?.label ?? humanize(kind);
export const isDestructiveKind = (kind: string): boolean => STEP_KINDS[kind]?.destructive === true;

/** Areas to draw: the known order first, then any area the server sent that this build does not know. */
export function orderAreas(seen: Iterable<string>): string[] {
  const set = new Set<string>(AREA_ORDER);
  for (const a of seen) set.add(a);
  const known = AREA_ORDER.filter((a) => set.has(a));
  const extra = [...set].filter((a) => !(AREA_ORDER as readonly string[]).includes(a)).sort();
  return [...known, ...extra];
}

export function addableKinds(area: string): string[] {
  return Object.entries(STEP_KINDS)
    .filter(([, k]) => k.area === area && k.orgAddable)
    .map(([kind]) => kind);
}

export const ownerLabel = (origin: 'siliconx' | 'org'): string => (origin === 'siliconx' ? 'SiliconX' : 'Org');

export interface FitBadge {
  tone: 'green' | 'amber' | 'neutral';
  text: string;
}

/** The fit of a search result for the machine on the bench. No fit (no code, or no discovery) shows no badge. */
export function fitBadges(fit: BenchPlanSearchResult['fit']): FitBadge[] {
  if (!fit) return [];
  const out: FitBadge[] = [];
  if (fit.gaps > 0) out.push({ tone: 'amber', text: fit.gaps === 1 ? '1 gap' : `${fit.gaps} gaps` });
  if (fit.outOfScope > 0) out.push({ tone: 'neutral', text: fit.outOfScope === 1 ? '1 part out of scope' : `${fit.outOfScope} parts out of scope` });
  if (fit.fits && fit.gaps === 0) out.unshift({ tone: 'green', text: 'fits this hardware' });
  return out;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return 'under a minute';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `about ${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `about ${h} h` : `about ${h} h ${m} min`;
}

/** Last 4 characters of the host service tag (all of it when shorter). Null when the host reported none. */
export function serviceTagTail(tag: string | null | undefined): string | null {
  const t = (tag ?? '').trim();
  if (t.length === 0) return null;
  return t.slice(-4);
}

/** O7: the typed text must equal the tail of the service tag the bench reported (case-insensitive). */
export function serviceTagConfirmed(tag: string | null | undefined, typed: string): boolean {
  const tail = serviceTagTail(tag);
  if (tail === null) return false;
  return typed.trim().toUpperCase() === tail.toUpperCase();
}

type Part = BenchHardwareFound['parts'][number];

export interface PreviewDeviceGroup {
  deviceKey: string;
  part: Part | undefined;
  steps: BenchPlanPreview['steps'];
}

/** Preview steps grouped by device, in device order. */
export function groupPreview(preview: BenchPlanPreview, parts: readonly Part[]): PreviewDeviceGroup[] {
  const byKey = new Map<string, PreviewDeviceGroup>();
  for (const s of preview.steps) {
    let g = byKey.get(s.deviceKey);
    if (!g) {
      g = { deviceKey: s.deviceKey, part: parts.find((p) => p.key === s.deviceKey), steps: [] };
      byKey.set(s.deviceKey, g);
    }
    g.steps.push(s);
  }
  const idx = (k: string) => Number(k.slice('device:'.length));
  return [...byKey.values()].sort((a, b) => idx(a.deviceKey) - idx(b.deviceKey));
}

/** Parts that get tests (and become assets): devices with at least one step that is not a gap or an unticked step. */
export function assetDeviceCount(preview: BenchPlanPreview): number {
  return new Set(preview.steps.filter((s) => s.status !== 'gap' && s.deselected !== true).map((s) => s.deviceKey)).size;
}

/** What changed between two previews of the same plan, for the "review again" notice after preview_stale. */
export function previewChanges(prev: BenchPlanPreview, next: BenchPlanPreview): string[] {
  const out: string[] = [];
  if (prev.planId !== next.planId || prev.version !== next.version) out.push(`Plan version is now v${next.version}.`);
  if (prev.mandatorySetVersion !== next.mandatorySetVersion) out.push(`SiliconX mandatory rules are now version ${next.mandatorySetVersion}.`);
  if (prev.gaps !== next.gaps) out.push(`Gaps changed from ${prev.gaps} to ${next.gaps}.`);
  if (prev.outOfScope !== next.outOfScope) out.push(`Parts out of scope changed from ${prev.outOfScope} to ${next.outOfScope}.`);
  if (prev.wipeDevices.length !== next.wipeDevices.length) {
    out.push(`Devices to wipe changed from ${prev.wipeDevices.length} to ${next.wipeDevices.length}.`);
  }
  if (prev.steps.length !== next.steps.length) out.push(`Steps changed from ${prev.steps.length} to ${next.steps.length}.`);
  if (prev.estimatedSeconds !== next.estimatedSeconds) {
    out.push(`Estimated time changed from ${formatDuration(prev.estimatedSeconds)} to ${formatDuration(next.estimatedSeconds)}.`);
  }
  if (out.length === 0) out.push('The machine, the plan or the SiliconX rules were updated.');
  return out;
}

export function gapCopy(): string {
  return 'No SiliconX test for this part yet';
}

/**
 * Fixed copy per plan error code, for search, preview and approve. The server message is never shown. Returns null for a code
 * this screen has no plan-specific copy for (the caller falls back to its generic copy).
 */
export function planErrorMessage(code: string | undefined, status?: number): string | null {
  switch (code) {
    case 'plan_unsupported': return 'This bench image does not support plans. Update the bench image, then pair again.';
    case 'plan_required': return 'Choose a plan to approve this machine.';
    case 'plan_outside_selection': return 'The test selection does not belong to this plan. Review the plan again.';
    case 'plan_not_found': return 'That plan is no longer available. Choose another plan.';
    case 'plan_stale':
    case 'preview_stale':
    case 'plan_invalid': return 'What will run changed since you reviewed it. Review it again.';
    case 'no_mandatory_set': return 'SiliconX has not published its mandatory tests yet, so a plan cannot be approved. Contact SiliconX support.';
    case 'too_many_searches': return 'Too many plan searches for this machine. Wait 10 minutes, then try again.';
    case 'invalid_query': return 'The search is not valid.';
    case 'plan_forbidden': return 'Your verified sign-in could not be confirmed for this approval. Sign in again.';
    case 'plans_disabled': return 'Plans are not available right now.';
    case 'name_taken': return 'A plan with that name already exists. Choose another name.';
    case 'limit_reached': return 'A plan limit was reached (plans per organization, versions per plan, or steps per plan).';
    case 'step_not_allowed': return 'A step is not allowed here.';
    case 'unknown_area': return 'An area is not available.';
    case 'archived': return 'That plan is archived.';
    case 'admin_required':
    case 'not_authorized': return 'Only an org admin can change plans.';
    case 'invalid_argument':
    case 'invalid_body': return 'The plan was not accepted.';
    case 'try_again': return 'The system is busy. Try again in a moment.';
    default: break;
  }
  if (status === 429) return 'Too many requests. Wait a few minutes, then try again.';
  return null;
}

/** no_mandatory_set is the one plan failure no retry can fix. */
export const isNotRetryable = (code: string | undefined): boolean => code === 'no_mandatory_set';

// ---------------------------------------------------------------------------------------------------------------------
// Plan builder draft model
// ---------------------------------------------------------------------------------------------------------------------

/** An org step in the builder. `on` false = shown unticked and left out when saved. */
export interface DraftStep {
  area: string;
  stepKind: string;
  on: boolean;
}

export interface PlanDraft {
  /** area -> in scope. `inventory` is always true. */
  scope: Record<string, boolean>;
  steps: DraftStep[];
}

/** A blank draft: every area in scope; the mandatory rows come from the live set, not the draft. */
export function blankDraft(extraAreas: Iterable<string> = []): PlanDraft {
  const scope: Record<string, boolean> = {};
  for (const a of orderAreas(extraAreas)) scope[a] = true;
  return { scope, steps: [] };
}

/** Draft from a saved plan (`areas` lists only the in-scope areas; every other known area is out of scope). */
export function draftFromPlan(
  plan: { areas: readonly string[]; steps: ReadonlyArray<{ area: string; stepKind: string }> },
  extraAreas: Iterable<string> = [],
): PlanDraft {
  const all = orderAreas([...plan.areas, ...plan.steps.map((s) => s.area), ...extraAreas]);
  const inScope = new Set(plan.areas);
  const scope: Record<string, boolean> = {};
  for (const a of all) scope[a] = a === 'inventory' || inScope.has(a);
  return { scope, steps: plan.steps.map((s) => ({ area: s.area, stepKind: s.stepKind, on: true })) };
}

/** The save request body: every area with its flag, and the ticked steps of in-scope areas. */
export function draftToPayload(d: PlanDraft): {
  areas: Array<{ area: string; inScope: boolean }>;
  steps: Array<{ area: string; stepKind: string }>;
} {
  const areas = Object.entries(d.scope).map(([area, inScope]) => ({ area, inScope: area === 'inventory' ? true : inScope }));
  const steps = d.steps.filter((s) => s.on && d.scope[s.area] !== false).map((s) => ({ area: s.area, stepKind: s.stepKind }));
  return { areas, steps };
}

export function draftChanged(a: PlanDraft, b: PlanDraft): boolean {
  return JSON.stringify(draftToPayload(a)) !== JSON.stringify(draftToPayload(b));
}

/** "+ Add test": a kind joins the area, or re-ticks the row that is already there. */
export function addStep(d: PlanDraft, area: string, stepKind: string): PlanDraft {
  const has = d.steps.some((s) => s.area === area && s.stepKind === stepKind);
  return {
    ...d,
    steps: has
      ? d.steps.map((s) => (s.area === area && s.stepKind === stepKind ? { ...s, on: true } : s))
      : [...d.steps, { area, stepKind, on: true }],
  };
}

export function toggleStep(d: PlanDraft, area: string, stepKind: string, on: boolean): PlanDraft {
  return { ...d, steps: d.steps.map((s) => (s.area === area && s.stepKind === stepKind ? { ...s, on } : s)) };
}

export function setScope(d: PlanDraft, area: string, inScope: boolean): PlanDraft {
  if (area === 'inventory') return d;
  return { ...d, scope: { ...d.scope, [area]: inScope } };
}

/** Wording of a locked row's condition. */
export function mandatoryCondition(area: string): string {
  switch (area) {
    case 'inventory': return 'always';
    case 'gpu': return 'if GPU present';
    case 'ram': return 'if memory present';
    case 'cpu': return 'if CPU present';
    case 'storage': return 'if drive present';
    case 'chassis': return 'if chassis present';
    default: return 'if present';
  }
}
